import { useState, useCallback } from 'react';
import { TabItem, TabsProps } from './contracts';
import { GeneratePage } from '../../../cms/GeneratePage';
import { useCmsComponentsMap } from '../../../cms/GeneratePage/CmsComponentsMapContext';
import { CmsComponentsMap } from '../../../cms/GeneratePage/contracts';
import { trackTabClick } from '../../../utils/tracking';
import { useSafeSearchParams, useSafeSetSearchParams } from '../../../utils/useSafeSearchParams';
import { useSafeLocationKey } from '../../../utils/useSafeLocationKey';
import { resolveTabIndex } from './resolveTabIndex';

/**
 * `/clubs?tab=east` opens the East tab. Reading is always on; WRITING the param back on a tab
 * click is opt-in per booking via `syncTabToUrl`, so no existing Tabs starts mutating the URL.
 */
const TAB_QUERY_PARAM = 'tab';
/** The registry a tab's layout renders from when nothing supplies one: no components, so nothing to render. */
const NO_COMPONENTS: CmsComponentsMap = {};

/** Must match resolveTabIndex's slugging, or a written param would not read back. */
const slugify = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, '-');

export function Tabs({ title, tabs, className, resetQueryParams, syncTabToUrl }: TabsProps) {
  // SC-1213: a Tab's layout is rendered by a nested GeneratePage, which needs the same
  // registry the outer page is using — the static one under SSR. Read from context because
  // Row renders Tabs through the map and cannot pass it as a prop.
  //
  // Every place that renders Tabs supplies one: GeneratePage on the web and the server, and
  // the admin page builder (CmsPageBuilder) for its canvas, drag preview and dialogs. Importing
  // the web registry here as a fallback instead would put Tabs in an import cycle with it,
  // since that registry imports Tabs. With none supplied, a tab's layout renders its rows
  // and no components. On the server the provider is always present — asserted by the
  // nested-Tabs SSR test, not assumed.
  const componentsMap = useCmsComponentsMap() ?? NO_COMPONENTS;
  const searchParams = useSafeSearchParams();
  const locationKey = useSafeLocationKey();
  const requestedIndex = resolveTabIndex(tabs, searchParams.get(TAB_QUERY_PARAM));
  const setSearchParams = useSafeSetSearchParams();

  /**
   * The URL is the source of truth; a click is an override that lives until the next
   * navigation. Derived during render rather than synced by an effect, which is what makes
   * the server render the requested tab — effects never run there.
   *
   * Scoped to `locationKey` rather than to the param value, because react-router issues a new
   * key even for a push to the URL already displayed. That is the difference between "the
   * visitor followed a link again" (discard the override — they asked for that tab) and
   * "React re-rendered" (keep it). Without it, clicking away from `?tab=east` and then
   * clicking the footer's East link again did nothing at all.
   */
  const [override, setOverride] = useState<{ key: string | null; index: number } | null>(null);
  const liveOverride = override && override.key === locationKey ? override.index : null;

  // `-1` means "the param names no tab" and resolves to the first tab in BOTH directions —
  // arriving at /clubs directly and navigating to it from /clubs?tab=east render the same.
  const desiredIndex = liveOverride ?? Math.max(requestedIndex, 0);
  // Clamped, because a seeded index can outlive the tabs it indexed: a page revision that
  // drops a division would otherwise leave the strip with nothing selected and no panel.
  const activeTabIndex = Math.min(desiredIndex, Math.max((tabs?.length ?? 0) - 1, 0));

  // Takes the tab rather than an index so the slug comes from the object already in scope at
  // the button — no re-lookup, and no unreachable "index out of range" branch to cover.
  const handleTabClick = useCallback(
    (index: number, tab: TabItem) => {
      trackTabClick(tab.label);
      setOverride({ key: locationKey, index });

      const resets = resetQueryParams ?? [];
      if (resets.length === 0 && !syncTabToUrl) {
        return;
      }

      // ONE navigation per click. Resetting and syncing through separate setSearchParams calls
      // would fire two. The `tab` set comes after the deletes so a config that lists `tab` in
      // resetQueryParams cannot wipe the value just written.
      setSearchParams(
        (params) => {
          resets.forEach((param) => params.delete(param));
          if (syncTabToUrl) {
            // Falls back to the id because stored CMS config is untyped — a tab that reached
            // the database without a label would otherwise slugify undefined and throw.
            params.set(TAB_QUERY_PARAM, slugify(tab.label ?? tab.id));
          }
        },
        // Replace, so Back leaves the page rather than stepping through every tab clicked.
        // Only when syncing — a bare reset keeps the push behaviour it has always had.
        syncTabToUrl ? { replace: true } : undefined
      );
    },
    [resetQueryParams, setSearchParams, syncTabToUrl, locationKey]
  );

  if (!tabs || tabs.length === 0) {
    return null;
  }

  const activeTab = tabs[activeTabIndex];

  return (
    <div className={`cms-tabs ${className ?? ''}`}>
      {title && <h2 className="cms-tabs__title">{title}</h2>}

      <div className="cms-tabs__header">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            className={`cms-tabs__tab small-text ${index === activeTabIndex ? 'cms-tabs__tab--active' : ''}`}
            onClick={() => handleTabClick(index, tab)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="cms-tabs__content">
        {activeTab && (activeTab.layout || activeTab.children) && (
          <div className="cms-tabs__panel small-text">
            {activeTab.layout ? (
              <GeneratePage {...activeTab.layout} componentsMap={componentsMap} />
            ) : (
              activeTab.children
            )}
          </div>
        )}
      </div>
    </div>
  );
}
